import { useState, type ReactElement } from 'react';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import styles from './ReservationBoardCalendar.module.css';

type RsShareUrlSectionProps = {
  shareUrl: string | null | undefined;
  onIssue: () => void;
  onRevoke?: () => void;
  issuing?: boolean;
  revoking?: boolean;
};

type ShareUrlIconTipProps = {
  id: string;
  title: string;
  description: string;
  children: ReactElement;
};

function ShareUrlIconTip({ id, title, description, children }: ShareUrlIconTipProps) {
  return (
    <OverlayTrigger
      placement="top"
      delay={{ show: 250, hide: 100 }}
      overlay={
        <Tooltip id={id} className={styles.shareUrlIconTooltip}>
          <div className={styles.shareUrlTooltipTitle}>{title}</div>
          <div className={styles.shareUrlTooltipDesc}>{description}</div>
        </Tooltip>
      }
    >
      <span className="d-inline-flex">{children}</span>
    </OverlayTrigger>
  );
}

type ShareUrlIconButtonProps = {
  tipId: string;
  title: string;
  description: string;
  iconClass: string;
  onClick: () => void;
  disabled?: boolean;
  /** 発行ボタンだけラベル文字を併記 */
  showLabel?: boolean;
};

function ShareUrlIconButton({
  tipId,
  title,
  description,
  iconClass,
  onClick,
  disabled = false,
  showLabel = false,
}: ShareUrlIconButtonProps) {
  return (
    <ShareUrlIconTip id={tipId} title={title} description={description}>
      <button
        type="button"
        className={showLabel ? `btn btn-sm ${styles.iconActionBtn}` : styles.shareUrlIconBtn}
        onClick={onClick}
        disabled={disabled}
        aria-label={title}
        style={disabled ? { pointerEvents: 'none' } : undefined}
      >
        <i className={`fas ${iconClass}`} aria-hidden />
        {showLabel ? <span className="ms-1 small">{title}</span> : null}
      </button>
    </ShareUrlIconTip>
  );
}

export default function RsShareUrlSection({
  shareUrl,
  onIssue,
  onRevoke,
  issuing = false,
  revoking = false,
}: RsShareUrlSectionProps) {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);
  /** 閉じている間は操作ボタンをマウントしない（a11y ツリーにコピー等が残らない） */
  const [open, setOpen] = useState(false);

  const handleCopy = async () => {
    if (!shareUrl) return;
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      /* clipboard 不可時は無視 */
    }
  };

  if (!shareUrl) {
    return (
      <div className={styles.shareUrlCompact}>
        <ShareUrlIconButton
          tipId="rs-share-url-issue"
          title={t('reservation.board.shareUrlIssueTitle', { defaultValue: 'URLを発行' })}
          description={t('reservation.board.shareUrlIssueDesc', {
            defaultValue: 'この予約表を開くためのURLを作成します。作成後はコピーして共有できます。',
          })}
          iconClass="fa-link"
          onClick={onIssue}
          disabled={issuing}
          showLabel
        />
      </div>
    );
  }

  return (
    <details
      className={styles.shareUrlDetails}
      open={open}
      onToggle={(e) => setOpen((e.target as HTMLDetailsElement).open)}
    >
      <summary className={styles.shareUrlSummary}>
        {t('reservation.board.shareUrlLabel', { defaultValue: '共有URL（ログイン不要）' })}
        <span className={styles.shareUrlSummaryHint}>
          {t('reservation.board.shareUrlToggleHint', { defaultValue: 'コピー・再発行' })}
        </span>
      </summary>
      {open ? (
        <div className={styles.shareUrlCompactInner}>
          <div className={styles.shareUrlFieldWrap}>
            <div className={styles.shareUrlField}>
              <span className={styles.shareUrlText} title={shareUrl}>
                {shareUrl}
              </span>
              <div className={styles.shareUrlIconGroup}>
                <ShareUrlIconButton
                  tipId="rs-share-url-copy"
                  title={t('reservation.board.shareUrlCopyTitle', { defaultValue: 'コピー' })}
                  description={t('reservation.board.shareUrlCopyDesc', {
                    defaultValue:
                      '表示されている共有URLをクリップボードにコピーします。メールやチャットで共有するときに使います。',
                  })}
                  iconClass="fa-copy"
                  onClick={handleCopy}
                />
                <ShareUrlIconButton
                  tipId="rs-share-url-reissue"
                  title={t('reservation.board.shareUrlReissueTitle', { defaultValue: 'URLを再発行' })}
                  description={t('reservation.board.shareUrlReissueDesc', {
                    defaultValue:
                      '新しいURLを発行します。再発行後は以前のURLでは開けなくなります。漏洩が心配なときに使います。',
                  })}
                  iconClass="fa-sync-alt"
                  onClick={onIssue}
                  disabled={issuing}
                />
                {onRevoke ? (
                  <ShareUrlIconButton
                    tipId="rs-share-url-revoke"
                    title={t('reservation.board.shareUrlRevokeTitle', { defaultValue: '共有を停止' })}
                    description={t('reservation.board.shareUrlRevokeDesc', {
                      defaultValue:
                        '共有URLを無効にします。停止後はこのURLから予約表を開けなくなります。',
                    })}
                    iconClass="fa-trash-alt"
                    onClick={onRevoke}
                    disabled={revoking}
                  />
                ) : null}
              </div>
            </div>
          </div>
          {copied ? (
            <p className={styles.shareUrlCopiedMsg} role="status">
              {t('reservation.board.shareUrlCopied', {
                defaultValue: 'URLをクリップボードにコピーしました。',
              })}
            </p>
          ) : null}
        </div>
      ) : null}
    </details>
  );
}
